import { isNotDefined } from '@sgme/fp';

import { monitorTimeout } from '@/api/monitorTimeout.ts';
import type { DealRejected, DealReply, FillReport } from '@/api/signalR/messages/execution.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { forwardAccumulatorApi } from '@/store/forwardAccumulator/api/index.ts';
import { selectForwardAccumulator } from '@/store/forwardAccumulator/selectors.ts';
import { forwardAccumulatorSlice } from '@/store/forwardAccumulator/slice.ts';
import type { ClientsWay } from '@/store/forwardAccumulator/state.ts';
import { fillReportReceived, getExecution, GrowlSlice, type GrowUpdate } from '@/store/growl/slice.ts';
import { updateGrowlApiErrorThunk } from '@/store/growl/updateGrowlApiErrorThunk.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { SECOND_IN_MILLIS } from '@/utils/time.ts';
import { toBase64 } from '@/utils/toBase64.ts';

interface ExecuteForwardAccumulatorTradeBody {
  rfsId: string;
  quoteId: string;
  email64NotificationList: string;
  userLocalDateTime: string;
  lastQuoteFromBackTimestamp: number;
  lastQuoteReceivedByFrontTimestamp: number;
  executionClickByFrontTimestamp: number;
  way: 'Ask';
}

export interface ExecuteForwardAccumulatorTradeParams {
  notificationEmail: string;
  tileId: string;
  quoteId: string;
  rfsId: string;
  accountId: number;
  currency1: string;
  currency2: string;
  notional: number;
  notionalCurrencyIndex: CurrencyIndex;
  userLocalDateTime: string;
  strike: number;
  premium: string;
  premiumCurrencyIndex: CurrencyIndex;
  amountMode: 'Fixing' | 'Total';
  firstFixing: {
    date: string;
    tenor: string | null | undefined;
  };
  expiry: {
    date: string;
    tenor: string | null | undefined;
  };
  fixingFrequency: string | undefined;
  numberOfFixings: number | undefined;
  accuType: 'Standard' | 'Permanent';
  clientsWay: ClientsWay;
  akoTrigger: number | undefined;
  ekiTrigger: number | undefined;
  step: number | null | undefined;
  lastQuoteReceivedByFrontTimestamp: number;
  lastQuoteFromBackTimestamp: number;
}

// TODO ABO, check how to get dates/timestamps correctly
export const executeForwardAccumulatorRfs = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<unknown, ExecuteForwardAccumulatorTradeParams>({
    query: (body) => {
      return {
        url: '/fxOption/rfs/execute',
        method: 'POST',
        body: {
          lastQuoteFromBackTimestamp: body.lastQuoteFromBackTimestamp,
          lastQuoteReceivedByFrontTimestamp: body.lastQuoteReceivedByFrontTimestamp,
          executionClickByFrontTimestamp: Date.now(),
          email64NotificationList: toBase64(body.notificationEmail),
          quoteId: body.quoteId,
          rfsId: body.rfsId,
          userLocalDateTime: body.userLocalDateTime,
          way: 'Ask',
        } as ExecuteForwardAccumulatorTradeBody,
      };
    },

    async onQueryStarted(request, { queryFulfilled, getState, dispatch }) {
      const { tileId, rfsId } = request;

      const stopMonitor = monitorTimeout(rfsId, dispatch, {
        onExecFail: () => {
          dispatchForwardAccuExecutionRejected(tileId, rfsId, dispatch);
        },
        onExecSuccess: () => {
          dispatchForwardAccuExecutionSuccess(tileId, rfsId, dispatch);
        },
        getPendingExecutionDetails: () => {
          const execution = getExecution(getState() as AppState, rfsId);
          if (
            isNotDefined(execution) ||
            execution.type !== 'forwardAccumulatorDealExecution' ||
            execution.data.status !== 'pending'
          ) {
            return undefined;
          }

          return {
            clientId: execution.data.data.accountId,
            currencyPair: `${execution.data.data.currency1}/${execution.data.data.currency2}`,
            productType: execution.data.productName,
            tradeDate: execution.data.data.tradeDate,
          };
        },
      });

      try {
        await queryFulfilled;
      } catch (error) {
        stopMonitor();
        dispatch(updateGrowlApiErrorThunk({ growlId: rfsId, error }));
        dispatchForwardAccuExecutionRejected(tileId, rfsId, dispatch);
      }
    },

    async onCacheEntryAdded(params, { getState, dispatch, cacheEntryRemoved }) {
      const currentState = getState() as AppState;
      const tile = selectForwardAccumulator(currentState, params.tileId);

      if (isNotDefined(tile)) {
        console.error('tile not found', params.tileId);

        return;
      }

      dispatch(
        GrowlSlice.actions.create({
          growlId: params.rfsId,
          growl: {
            type: 'forwardAccumulatorDealExecution',
            data: {
              status: 'pending',
              tileId: params.tileId,
              productName: 'FxForwardAccumulator',
              data: {
                way: params.clientsWay === 'Buy' ? 'Ask' : 'Bid',
                currency1: params.currency1,
                currency2: params.currency2,
                notional: params.notional,
                notionalCurrencyIndex: params.notionalCurrencyIndex,
                accountId: params.accountId,
                tradeDate: params.userLocalDateTime,
                expiry: params.expiry,
                strike: params.strike,
                accuType: params.accuType,
                premium: params.premium,
                premiumCurrencyIndex: params.premiumCurrencyIndex,
                firstFixing: params.firstFixing,
                fixingFrequency: params.fixingFrequency,
                amountMode: params.amountMode,
                numberOfFixings: params.numberOfFixings,
                akoTrigger: params.akoTrigger,
                ekiTrigger: params.ekiTrigger,
                step: params.step,
              },
            },
            createdAt: Date.now(),
            options: { closeable: false },
          },
        }),
      );

      dispatch(
        forwardAccumulatorSlice.actions.forwardAccumulatorPending({ tileId: params.tileId, executionId: params.rfsId }),
      );

      const handleReceiveDealReply = buildHandleReceiveDealReply(params, dispatch);
      const handleReceiveDealRejected = buildHandleReceiveDealRejected(params, dispatch);
      const handleReceiveFillReport = buildHandleReceiveFillReport(params, dispatch);

      const signalRConnector = getSignalRConnector();
      signalRConnector.on('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.on('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.on('ReceiveFillReport', handleReceiveFillReport);

      await cacheEntryRemoved; // TODO: remove automatic cache removed after 1 hour

      signalRConnector.off('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.off('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.off('ReceiveFillReport', handleReceiveFillReport);
    },
  });
};

function buildHandleReceiveDealReply(params: ExecuteForwardAccumulatorTradeParams, dispatch: AppDispatch) {
  return (dealReply: DealReply) => {
    const executionId = dealReply.Execute.ExecutionId;
    const rfsId = params.rfsId;

    if (executionId !== rfsId) {
      return;
    }

    dispatchForwardAccuExecutionSuccess(params.tileId, rfsId, dispatch);
  };
}

const dispatchForwardAccuExecutionSuccess = (tileId: string, rfsId: string, dispatch: AppDispatch) => {
  dispatch(
    GrowlSlice.actions.update({
      growlId: rfsId,
      data: { status: 'integrated' },
      options: {
        closeable: true,
      },
    }),
  );

  dispatch(forwardAccumulatorSlice.actions.forwardAccumulatorExecutionSucceed({ tileId }));

  setTimeout(() => {
    dispatch(forwardAccumulatorSlice.actions.forwardAccumulatorExecutionReset({ tileId }));
  }, 2 * SECOND_IN_MILLIS);
};

function buildHandleReceiveFillReport(params: ExecuteForwardAccumulatorTradeParams, dispatch: AppDispatch) {
  return (fillReport: FillReport) => {
    const executionId = fillReport.Execute.ExecutionId;
    const rfsId = params.rfsId;

    if (executionId !== rfsId) {
      return;
    }

    dispatch(fillReportReceived({ growlId: executionId, tradeIds: fillReport.BackOfficeReply.TradeIds }));

    dispatch(forwardAccumulatorApi.internalActions.removeMutationResult({ fixedCacheKey: executionId }));

    dispatch(forwardAccumulatorSlice.actions.forwardAccumulatorExecutionSucceed({ tileId: params.tileId }));
  };
}

function buildHandleReceiveDealRejected(params: ExecuteForwardAccumulatorTradeParams, dispatch: AppDispatch) {
  return (rejectedDeal: DealRejected) => {
    const executionId = rejectedDeal.Execute.ExecutionId;
    const rfsId = params.rfsId;

    if (executionId !== rfsId) {
      return;
    }

    const data: GrowUpdate = {
      status: 'rejected',
      errorCode: rejectedDeal.DealReject.ErrorCode,
      multipassErrorCode: rejectedDeal.DealReject.MultipassErrorCode,
      reason: rejectedDeal.DealReject.ErrorMessage,
      sessionId: rejectedDeal.DealReject.MultipassSessionId,
    };
    dispatch(GrowlSlice.actions.update({ growlId: params.rfsId, data, options: { closeable: true } }));
    dispatchForwardAccuExecutionRejected(params.tileId, rfsId, dispatch);
  };
}

const dispatchForwardAccuExecutionRejected = (tileId: string, rfsId: string, dispatch: AppDispatch) => {
  dispatch(forwardAccumulatorApi.internalActions.removeMutationResult({ fixedCacheKey: rfsId }));
  dispatch(forwardAccumulatorSlice.actions.forwardAccumulatorExecutionRejected({ tileId }));
  setTimeout(
    () => dispatch(forwardAccumulatorSlice.actions.forwardAccumulatorRejectedExecutionReset({ tileId })),
    2 * SECOND_IN_MILLIS,
  );
};
